import React, { FunctionComponent, useEffect, useState } from 'react'
import { Alert, Button, Card, Descriptions, Popconfirm, Space, Spin, Tag, Typography, message } from 'antd'
import { MergeCellsOutlined, ReloadOutlined } from '@ant-design/icons'
import { Link } from 'react-router'
import { InstrumentsService } from '../api/services.gen'
import { InstrumentDto, SimilarInstrumentsGroupDto } from '../api/types.gen'

const InstrumentsMergePage: FunctionComponent = () => {
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState<SimilarInstrumentsGroupDto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [mergingGroup, setMergingGroup] = useState<number | null>(null)

  const fetchSimilarInstruments = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await InstrumentsService.findSimilarInstruments()
      setGroups(result.groups)
    } catch (e: any) {
      console.error('Failed to fetch similar instruments:', e)
      setError(e.message || 'Failed to load similar instruments')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSimilarInstruments()
  }, [])

  const mergeGroup = async (groupIndex: number, target: InstrumentDto) => {
    const group = groups[groupIndex]
    const sources = group.instruments.filter(i => i.code !== target.code)

    setMergingGroup(groupIndex)
    try {
      for (const source of sources) {
        await InstrumentsService.mergeInstruments({
          requestBody: {
            sourceCode: source.code,
            targetCode: target.code,
          },
        })
      }
      message.success(`Merged ${sources.map(s => s.code).join(', ')} into ${target.code}`)
      setGroups(current => current.filter((_, index) => index !== groupIndex))
    } catch (e: any) {
      console.error('Failed to merge instruments:', e)
      message.error(e.message || 'Failed to merge instruments')
    } finally {
      setMergingGroup(null)
    }
  }

  if (loading) {
    return (
      <div>
        <Typography.Title level={3}>Merge instruments</Typography.Title>
        <Space>
          <Spin />
          <Typography.Text type="secondary">Scanning instruments for duplicates...</Typography.Text>
        </Space>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <Typography.Title level={3}>Merge instruments</Typography.Title>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    )
  }

  return (
    <div>
      <Typography.Title level={3}>Merge instruments</Typography.Title>
      <Space style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary">
          {groups.length > 0
            ? `${groups.length} group${groups.length > 1 ? 's' : ''} of probably duplicated instruments found`
            : 'No duplicated instruments found'}
        </Typography.Text>
        <Button icon={<ReloadOutlined />} onClick={fetchSimilarInstruments}>Rescan</Button>
      </Space>

      <Space direction="vertical" style={{ width: '100%' }}>
        {groups.map((group, groupIndex) => (
          <Card
            key={group.instruments.map(i => i.code).join('|')}
            size="small"
            title={
              <Space>
                <span>{group.instruments.map(i => i.code).join(' / ')}</span>
                {group.matchedBy.map(signal => (
                  <Tag key={signal} color="orange">matched by {signal}</Tag>
                ))}
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {group.instruments.map(instrument => (
                <Card key={instrument.code} size="small" type="inner">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Link to={`/instruments/instrument/${instrument.code}`}>
                        <b>{instrument.name || instrument.code}</b>
                      </Link>
                      <Typography.Text type="secondary">{instrument.code}</Typography.Text>
                    </Space>
                    <Descriptions size="small" column={3}>
                      {instrument.cik && <Descriptions.Item label="CIK">{instrument.cik}</Descriptions.Item>}
                      <Descriptions.Item label="Markets">
                        {instrument.markets && instrument.markets.length > 0
                          ? instrument.markets.map(market => (
                            <Tag key={market.id}>{market.symbol} on {market.market?.name || market.market?.id}{market.isin ? ` (${market.isin})` : ''}</Tag>
                          ))
                          : 'None'}
                      </Descriptions.Item>
                    </Descriptions>
                    <Popconfirm
                      title={`Keep ${instrument.code}?`}
                      description={`${group.instruments.filter(i => i.code !== instrument.code).map(i => i.code).join(', ')} will be merged into ${instrument.code} and deleted`}
                      onConfirm={() => mergeGroup(groupIndex, instrument)}
                      okText="Merge"
                    >
                      <Button
                        type="primary"
                        icon={<MergeCellsOutlined />}
                        loading={mergingGroup === groupIndex}
                        disabled={mergingGroup !== null && mergingGroup !== groupIndex}
                      >
                        Merge into this one
                      </Button>
                    </Popconfirm>
                  </Space>
                </Card>
              ))}
            </Space>
          </Card>
        ))}
      </Space>
    </div>
  )
}

export { InstrumentsMergePage }
